import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Layout, Trophy, Users, FolderOpen, Image, Loader2, LayoutDashboard, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';

interface SearchResult {
  id: string;
  label: string;
  hint?: string;
  path: string;
}

interface SearchGroups {
  posts: SearchResult[];
  events: SearchResult[];
  tournaments: SearchResult[];
  members: SearchResult[];
  documents: SearchResult[];
  media: SearchResult[];
}

const emptyGroups: SearchGroups = { posts: [], events: [], tournaments: [], members: [], documents: [], media: [] };

const PAGES: SearchResult[] = [
  { id: 'p-dashboard', label: 'Dashboard', path: '/admin' },
  { id: 'p-users', label: 'Benutzer', path: '/admin/users' },
  { id: 'p-training', label: 'Training', path: '/admin/training' },
  { id: 'p-training-requests', label: 'Anfragen', path: '/admin/training-requests' },
  { id: 'p-blog', label: 'Blog & News', path: '/admin/blog' },
  { id: 'p-events', label: 'Events', path: '/admin/events' },
  { id: 'p-media', label: 'Mediathek', path: '/admin/media' },
  { id: 'p-tournaments', label: 'Turniere', path: '/admin/tournaments' },
  { id: 'p-rankings', label: 'Clubranglisten', path: '/admin/club-rankings' },
  { id: 'p-files', label: 'Dateien', path: '/admin/files' },
  { id: 'p-banner', label: 'Banner', path: '/admin/banner' },
  { id: 'p-about', label: 'Über uns', path: '/admin/about' },
  { id: 'p-contact', label: 'Kontakt', path: '/admin/contact' },
  { id: 'p-analytics', label: 'Statistiken', path: '/admin/analytics' },
  { id: 'p-settings', label: 'Einstellungen', path: '/admin/settings' },
];

const escapeLike = (s: string) => s.replace(/[%_\\]/g, (m) => `\\${m}`);

export default function AdminSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<SearchGroups>(emptyGroups);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keyboard shortcut Ctrl/Cmd + K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    setQuery('');
    setGroups(emptyGroups);
  }, [open]);

  // Debounced search across tables
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setGroups(emptyGroups);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(async () => {
      const like = `%${escapeLike(term)}%`;
      const [posts, events, tournaments, members, documents, media] = await Promise.all([
        supabase.from('blog_posts').select('id, title, slug, published').is('deleted_at', null).ilike('title', like).limit(6),
        supabase.from('events').select('id, name, date').ilike('name', like).limit(6),
        supabase.from('tournament_results').select('id, name, date').ilike('name', like).limit(6),
        supabase.from('board_members').select('id, name, role').ilike('name', like).limit(6),
        supabase.from('documents').select('id, original_filename, category').ilike('original_filename', like).limit(6),
        supabase.from('gallery_images').select('id, title').ilike('title', like).limit(6),
      ]);
      if (cancelled) return;
      // Each result deep-links to the item itself (?edit=<id>) so the page opens it directly.
      const link = (page: string, q: string, id: string) => `${page}?q=${encodeURIComponent(q)}&edit=${id}`;
      setGroups({
        posts: (posts.data || []).map((p) => ({ id: p.id, label: p.title, hint: p.published ? 'Veröffentlicht' : 'Entwurf', path: link('/admin/blog', p.title, p.id) })),
        events: (events.data || []).map((e) => ({ id: e.id, label: e.name, hint: e.date, path: link('/admin/events', e.name, e.id) })),
        tournaments: (tournaments.data || []).map((t) => ({ id: t.id, label: t.name, hint: t.date, path: link('/admin/tournaments', t.name, t.id) })),
        members: (members.data || []).map((m) => ({ id: m.id, label: m.name, hint: m.role, path: link('/admin/about', m.name, m.id) })),
        documents: (documents.data || []).map((d) => ({ id: d.id, label: d.original_filename, hint: d.category || undefined, path: link('/admin/files', d.original_filename, d.id) })),
        media: (media.data || []).map((g) => ({ id: g.id, label: g.title, path: link('/admin/media', g.title, g.id) })),
      });
      setLoading(false);
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query]);

  const pageMatches = useMemo(() => {
    const t = query.trim().toLowerCase();
    return t ? PAGES.filter((p) => p.label.toLowerCase().includes(t)) : PAGES;
  }, [query]);
  const pagesHeading = query.trim() ? 'CMS-Seiten' : 'Schnellzugriff';

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const hasResults = Object.values(groups).some((g) => g.length > 0) || pageMatches.length > 0;

  const renderGroup = (heading: string, Icon: typeof FileText, items: SearchResult[]) =>
    items.length > 0 && (
      <div key={heading} className="px-2 pb-1">
        <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{heading}</p>
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => go(item.path)}
            className="flex w-full items-center rounded-sm px-2 py-2.5 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none"
          >
            <Icon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{item.label}</span>
            {item.hint && <span className="ml-auto pl-3 text-xs text-muted-foreground shrink-0">{item.hint}</span>}
          </button>
        ))}
      </div>
    );

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-8 gap-2 text-muted-foreground w-full max-w-xs sm:w-auto justify-start"
        onClick={() => setOpen(true)}
        aria-label="CMS durchsuchen"
      >
        <Search className="h-4 w-4" />
        <span className="truncate">Suchen…</span>
        <kbd className="ml-auto hidden md:inline-flex h-5 items-center rounded border bg-muted px-1.5 font-mono text-[10px]">
          Ctrl K
        </kbd>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="top-[10%] translate-y-0 w-[calc(100%-1.5rem)] max-w-lg overflow-hidden p-0 gap-0 [&>button]:hidden">
          <DialogTitle className="sr-only">CMS durchsuchen</DialogTitle>
          <div className="flex items-center gap-2 border-b px-3">
            <Search className="h-4 w-4 shrink-0 opacity-50" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Beiträge, Events, Turniere, Team, Dateien, Medien…"
              className="h-12 border-0 bg-transparent px-0 text-base sm:text-sm shadow-none focus-visible:ring-0"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              aria-label="Suchbegriff"
            />
            {query ? (
              <button type="button" onClick={() => setQuery('')} aria-label="Eingabe löschen" className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            ) : (
              <button type="button" onClick={() => setOpen(false)} aria-label="Schließen" className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto overflow-x-hidden py-1">
            {loading && (
              <div className="flex items-center gap-2 px-4 py-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> Suche läuft…
              </div>
            )}
            {!loading && !hasResults && <p className="py-6 text-center text-sm text-muted-foreground">Keine Treffer.</p>}
            {renderGroup('Beiträge', FileText, groups.posts)}
            {renderGroup('Events', Layout, groups.events)}
            {renderGroup('Turnierergebnisse', Trophy, groups.tournaments)}
            {renderGroup('Team', Users, groups.members)}
            {renderGroup('Dateien', FolderOpen, groups.documents)}
            {renderGroup('Medien', Image, groups.media)}
            {pageMatches.length > 0 && (
              <>
                {Object.values(groups).some((g) => g.length > 0) && <div className="my-1 h-px bg-border" />}
                {renderGroup(pagesHeading, LayoutDashboard, pageMatches)}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
